import type { Session } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes } from 'react-router-dom';

import { signIn } from './lib/brain';
import { supabase } from './lib/supabase';
import Login from './pages/Login';
import ReviewQueue from './pages/ReviewQueue';
import Setup from './pages/Setup';
import Spend from './pages/Spend';

/**
 * Dev-only silent sign-in.
 *
 * `__DEV_LOGIN__` is injected by vite.config.ts and is `null` in a build, so this cannot run in
 * production. It exists because typing the login on localhost is a chore while the dev server is
 * restarting five times an hour: the dev server hands the app the same static username and
 * password the login form asks for, and the app signs in through the same Worker route the form
 * uses. Nothing is bypassed - the pair is still checked, and the session that comes back is an
 * ordinary one, so RLS still decides what this page may read.
 *
 * One attempt per page load, cached as a promise because StrictMode mounts the effect twice and
 * two logins for one page load is one too many.
 */
let devSignInAttempt: Promise<string | null> | null = null;

function devSignInOnce(): Promise<string | null> {
  const credentials = __DEV_LOGIN__;
  if (!credentials) return Promise.resolve(null);
  devSignInAttempt ??= signIn(credentials.username, credentials.password).then((result) =>
    result.ok ? null : (result.error ?? 'the login was refused'),
  );
  return devSignInAttempt;
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  /** Set only when the dev auto sign-in was tried and the login refused or nothing answered. */
  const [devSignInError, setDevSignInError] = useState<string | null>(null);

  /**
   * The session comes from two places and nowhere else: storage (a session from a previous
   * visit, refreshed if needed) or a login. Both hand over an ordinary Supabase session; asking
   * the database whether the address is on `app_allowlist` used to be a third step here, and it
   * is gone on purpose - the login can only ever produce the dashboard's own account, so the
   * check could only ever fire when a *read* happened to fail, which is a way to be locked out
   * of a working dashboard by a hiccup. RLS remains the data boundary.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;

      // No session, and no dev credentials to fall back on: the login screen is the way in.
      if (data.session || !__DEV_LOGIN__) {
        setSession(data.session);
        setReady(true);
        return;
      }

      const failure = await devSignInOnce();
      if (cancelled) return;

      if (failure) {
        setDevSignInError(failure);
        setReady(true);
        return;
      }

      // Read the session back rather than trusting the auth event: the listener that caught
      // SIGNED_IN may belong to the StrictMode mount that was already torn down, which would
      // leave this waiting on a screen forever.
      const { data: signedIn } = await supabase.auth.getSession();
      if (cancelled) return;
      setSession(signedIn.session);
      setReady(true);
    })();

    // Refreshes and sign-outs land here too, so the dashboard follows the session it actually
    // holds rather than the one it had at load.
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });

    return () => {
      cancelled = true;
      data.subscription.unsubscribe();
    };
  }, []);

  if (!ready) {
    return (
      <div className="center muted">{__DEV_LOGIN__ ? 'Signing in…' : 'Loading…'}</div>
    );
  }
  if (!session) return <Login devSignInError={devSignInError} />;

  const actor = session.user.email ?? session.user.id;

  return (
    <BrowserRouter>
      <div className="app">
        <header className="topbar">
          <div className="brand">NestMatch Outreach</div>
          <nav>
            <NavLink to="/queue">Review queue</NavLink>
            <NavLink to="/setup">Setup</NavLink>
            <NavLink to="/spend">Spend</NavLink>
          </nav>
          <div className="who">
            <span className="muted small">{actor}</span>
            {__DEV_LOGIN__ ? (
              // Says which identity you are looking at, and why you did not choose it.
              // Sign out still works and drops you on the login screen; a reload then
              // signs in as this account again.
              <span className="badge">dev auto-login</span>
            ) : null}
            <button type="button" className="link" onClick={() => void supabase.auth.signOut()}>
              Sign out
            </button>
          </div>
        </header>

        <main>
          <Routes>
            <Route path="/" element={<Navigate to="/queue" replace />} />
            <Route path="/queue" element={<ReviewQueue actor={actor} />} />
            <Route path="/setup" element={<Setup actor={actor} />} />
            <Route path="/spend" element={<Spend />} />
            {/* Old bookmarks: the watchlist and the controls are one job, and now one page. */}
            <Route path="/sources" element={<Navigate to="/setup" replace />} />
            <Route path="/settings" element={<Navigate to="/setup" replace />} />
            <Route path="*" element={<Navigate to="/queue" replace />} />
          </Routes>
        </main>

        <footer className="muted small">
          This dashboard never posts anything anywhere. You post the reply by hand, or queue it and
          the poster on your own PC sends it; the queue records what happened either way. The Worker
          finds the posts, classifies them, drafts the replies — and decides whether anything may go.
        </footer>
      </div>
    </BrowserRouter>
  );
}
